<template>
    <div class="border rounded-md border-gray-200 flex flex-col items-start">
        <div class="editor-menu inline-flex flex-wrap gap-2 p-2">
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive('bold'),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().toggleBold().run()">
                <BoldIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive('italic'),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().toggleItalic().run()">
                <ItalicIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive('strike'),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().toggleStrike().run()">
                <StrikethroughIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive('bulletList'),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                 }"
                @click="editor && editor.chain().focus().toggleBulletList().run()">
                <ListBulletIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive('orderedList'),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().toggleOrderedList().run()">
                <NumberedListIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive({ textAlign: 'left' }),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().setTextAlign('left').run()" >
                <Bars3BottomLeftIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive({ textAlign: 'center' }),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().setTextAlign('center').run()" >
                <Bars3Icon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive({ textAlign: 'right' }),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="editor && editor.chain().focus().setTextAlign('right').run()" >
                <Bars3BottomRightIcon class="size-4" />
            </button>
            <button
                :class="{
                    'bg-gray-200': editor && editor.isActive('link'),
                    'border border-gray-200 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-gray-100': true
                }"
                @click="isLinkModalOpen = true">
                <LinkSlashIcon v-if="editor && editor.isActive('link')" class="size-4" />
                <LinkIcon v-else class="size-4" />
            </button>
        </div>

        <editor-content :editor="editor" class="user-description-editor min-h-30 p-2 border-t border-gray-200 sm:text-sm/6 w-full"/>

        <AddUrlToEditorModalComponent
            :open="isLinkModalOpen"
            @close="isLinkModalOpen = false"
            @confirm-action="addLink"
        />
    </div>
</template>

<script setup lang="ts">
import { watch, onBeforeUnmount, defineProps, defineEmits, ref } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import TextAlign from '@tiptap/extension-text-align'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import AddUrlToEditorModalComponent from "@/components/Users/Profile/Modals/AddUrlToEditorModalComponent.vue";
import {
    BoldIcon,
    ItalicIcon,
    StrikethroughIcon,
    ListBulletIcon,
    NumberedListIcon,
    Bars3Icon,
    Bars3BottomLeftIcon,
    Bars3BottomRightIcon,
    LinkIcon,
    LinkSlashIcon
} from "@heroicons/vue/20/solid";

const props = defineProps<{ modelValue: string }>()
const emit = defineEmits<{
    'update:modelValue': [string]
}>()

const isLinkModalOpen = ref(false)

const editor = useEditor({
    content: props.modelValue,
    extensions: [
        StarterKit.configure({
            codeBlock: false,
            blockquote: false,
        }),
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        Link.configure({
            autolink: true,
            openOnClick: false
        })
    ],
    onUpdate: ({ editor }) => {
        emit('update:modelValue', editor.getHTML())
    }
})

watch(
    () => props.modelValue,
    (newVal) => {
        if (editor.value && newVal !== editor.value.getHTML()) {
            editor.value.commands.setContent(newVal, false)
        }
    }
)

const addLink = (url: string | null) => {
    if (!editor.value) return
    if (url === null || url === '') {
        editor.value.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
        editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }

    isLinkModalOpen.value = false
}

onBeforeUnmount(() => {
    editor.value?.destroy()
})
</script>

<style lang="scss">
.user-description-editor {
    .tiptap {
        height: 100%;
    }
    .tiptap.ProseMirror-focused {
        outline: none;
    }

    a {
        color: #3b82f6; // Цвет ссылок
        text-decoration: underline;
    }

    ul {
        list-style-type: disc;
        padding-left: 1.5rem;
    }

    ol {
        list-style-type: decimal;
        padding-left: 1.5rem;
    }
}
</style>
